import { Link, useOutletContext, useParams } from "react-router";

import { useInfiniteQuery } from "@tanstack/react-query";

import { AlertCircleIcon, BugIcon, CircleIcon, PlusIcon, TagIcon, TicketIcon, UserIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { getProjectMembers, getProjectTags } from "@/features/projects/services/project";
import { Project, ProjectMember, ProjectTag } from "@/features/projects/types";
import { FilterPopover } from "@/features/tickets/components/FilterPopover";
import { SortPopover } from "@/features/tickets/components/SortPopover";
import { TicketRow } from "@/features/tickets/components/TicketRow";
import { TicketRowSkeleton } from "@/features/tickets/components/TicketRowSkeleton";
import { STATUS_DOT } from "@/features/tickets/config";
import { useTicketFilters } from "@/features/tickets/hooks/useTicketFilters";
import { getProjectTickets } from "@/features/tickets/services/ticket";
import { Status } from "@/features/tickets/types";
import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { InlineError } from "@/shared/components/InlineError";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

export default function ProjectTickets() {
  const { id } = useParams();
  const { permissions } = useOutletContext() as { project: Project; permissions: string[] };

  const canReadTickets = permissions.includes(PERMISSIONS.PROJECT_TICKETS_READ);
  const canCreateTickets = permissions.includes(PERMISSIONS.PROJECT_TICKETS_CREATE);
  const canReadMembers = permissions.includes(PERMISSIONS.PROJECT_MEMBERS_READ);
  const canReadTags = permissions.includes(PERMISSIONS.PROJECT_TAGS_READ);

  const { filters, sort, applyFilters, applySort, clearAllFilters, hasActiveFilters } = useTicketFilters();

  const { data, isPending, error, refetch, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_PROJECT_TICKETS, id, filters, sort],
    queryFn: ({ pageParam }) => getProjectTickets(id!, pageParam, filters, sort),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
    enabled: canReadTickets,
  });

  const {
    data: membersData,
    isPending: membersPending,
    error: membersError,
    refetch: refetchMembers,
    fetchNextPage: fetchNextMembersPage,
    isFetchingNextPage: isFetchingNextMembersPage,
    hasNextPage: hasNextMembersPage,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_PROJECT_MEMBERS, id],
    queryFn: ({ pageParam }) => getProjectMembers(id!, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
    enabled: canReadMembers,
  });

  const {
    data: tagsData,
    isPending: tagsPending,
    error: tagsError,
    refetch: refetchTags,
    fetchNextPage: fetchNextTagsPage,
    isFetchingNextPage: isFetchingNextTagsPage,
    hasNextPage: hasNextTagsPage,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_PROJECT_TAGS, id],
    queryFn: ({ pageParam }) => getProjectTags(id!, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
    enabled: canReadTags,
  });

  const tickets = data?.pages.flatMap((page) => page.tickets) ?? [];
  const members = membersData?.pages.flatMap((page) => page.members) ?? [];
  const tags = tagsData?.pages.flatMap((page) => page.tags) ?? [];

  const assigneeOptions = members.map((m: ProjectMember) => ({
    label: `${m.firstName} ${m.lastName}`,
    value: m.id,
  }));

  const { open: errorModalOpen, setOpen: setErrorModalOpen, handleRetry } = useErrorModal(error, refetch);
  const { open: membersErrorModalOpen, setOpen: setMembersErrorModalOpen, handleRetry: handleMembersRetry } = useErrorModal(membersError, refetchMembers);
  const { open: tagsErrorModalOpen, setOpen: setTagsErrorModalOpen, handleRetry: handleTagsRetry } = useErrorModal(tagsError, refetchTags);

  const statusCounts: Record<Status, number> = {
    Open: 0,
    "In Progress": 0,
    "In Review": 0,
    Resolved: 0,
    Closed: 0,
  };
  tickets.forEach((t) => {
    if (t.status in statusCounts) statusCounts[t.status as Status]++;
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold tracking-normal uppercase">Tickets</h2>
          <p className="text-sm text-muted-foreground">All tickets raised against this project.</p>
        </div>
        <PermissionTooltip allowed={canCreateTickets} message="You don't have permission to create tickets in this project.">
          <Button size="sm" className="shrink-0" asChild={canCreateTickets}>
            {canCreateTickets ? (
              <Link to={`/projects/${id}/tickets/new`}>
                <PlusIcon />
                New ticket
              </Link>
            ) : (
              <>
                <PlusIcon />
                New ticket
              </>
            )}
          </Button>
        </PermissionTooltip>
      </div>

      {error && <Error variant="modal" title="Failed to load tickets" error={error as AppError | Error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleRetry} />}

      <Card className="p-0 gap-0">
        <CardHeader className="flex flex-col gap-4 px-4 py-4">
          <div className="flex flex-wrap items-center gap-1">
            <FilterPopover label="Status" icon={CircleIcon} options={["Open", "In Progress", "In Review", "Resolved", "Closed"]} selected={filters.statuses} onApply={(values) => applyFilters({ ...filters, statuses: values })} />
            <FilterPopover label="Priority" icon={AlertCircleIcon} options={["Critical", "High", "Medium", "Low"]} selected={filters.priorities} onApply={(values) => applyFilters({ ...filters, priorities: values })} />
            <FilterPopover label="Type" icon={BugIcon} options={["Bug", "Feature", "Task", "Improvement"]} selected={filters.types} onApply={(values) => applyFilters({ ...filters, types: values })} />
            <FilterPopover
              label="Assignee"
              icon={UserIcon}
              options={assigneeOptions.map((o) => o.label)}
              selected={filters.assignees.map((assigneeId) => assigneeOptions.find((o) => o.value === assigneeId)?.label ?? assigneeId)}
              onApply={(labels) => {
                const ids = labels.map((label) => assigneeOptions.find((o) => o.label === label)?.value ?? label);
                applyFilters({ ...filters, assignees: ids });
              }}
              isLoading={membersPending}
              isFetchingNextPage={isFetchingNextMembersPage}
              hasNextPage={hasNextMembersPage}
              onLoadMore={() => fetchNextMembersPage()}
              error={membersError}
              errorModalOpen={membersErrorModalOpen}
              onErrorModalOpenChange={setMembersErrorModalOpen}
              onRetry={handleMembersRetry}
            />
            <FilterPopover
              label="Tag"
              icon={TagIcon}
              options={tags.map((t: ProjectTag) => t.name)}
              selected={filters.tags}
              onApply={(values) => applyFilters({ ...filters, tags: values })}
              isLoading={tagsPending}
              isFetchingNextPage={isFetchingNextTagsPage}
              hasNextPage={hasNextTagsPage}
              onLoadMore={() => fetchNextTagsPage()}
              error={tagsError}
              errorModalOpen={tagsErrorModalOpen}
              onErrorModalOpenChange={setTagsErrorModalOpen}
              onRetry={handleTagsRetry}
            />
            <SortPopover sort={sort} onApply={applySort} />
            {hasActiveFilters && (
              <button onClick={clearAllFilters} className="px-2 text-xs text-muted-foreground transition-colors hover:text-foreground">
                Clear all
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-4 pt-4">
            {(Object.entries(statusCounts) as [Status, number][]).map(([status, count]) => (
              <div key={status} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <div className={`size-2 shrink-0 ${STATUS_DOT[status]}`} />
                <span className="font-semibold text-foreground">{count}</span>
                {status}
              </div>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!canReadTickets ? (
            <EmptyState icon={BugIcon} title="No access" description="You don't have permission to view tickets." className="border border-dashed m-4" />
          ) : error && !errorModalOpen ? (
            <InlineError title="Failed to load tickets" description="Something went wrong while fetching tickets." onRetry={handleRetry} className="border border-dashed m-4" />
          ) : !isPending && tickets.length === 0 ? (
            <EmptyState
              icon={TicketIcon}
              title={hasActiveFilters ? "No tickets match your filters" : "No tickets yet"}
              description={hasActiveFilters ? "Try adjusting or clearing your filters." : "Create the first ticket to start tracking work."}
              action={
                hasActiveFilters ? (
                  <Button size="sm" variant="outline" onClick={clearAllFilters}>
                    Clear filters
                  </Button>
                ) : (
                  <PermissionTooltip allowed={canCreateTickets} message="You don't have permission to create tickets in this project.">
                    <Button size="sm" asChild={canCreateTickets}>
                      {canCreateTickets ? (
                        <Link to={`/projects/${id}/tickets/new`}>
                          <PlusIcon />
                          New ticket
                        </Link>
                      ) : (
                        <>
                          <PlusIcon />
                          New ticket
                        </>
                      )}
                    </Button>
                  </PermissionTooltip>
                )
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">ID</TableHead>
                  <TableHead className="w-8">Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-28">Priority</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="w-40">Assignee</TableHead>
                  <TableHead className="w-28">Reporter</TableHead>
                  <TableHead className="w-16">Comments</TableHead>
                  <TableHead className="w-24">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{isPending ? Array.from({ length: 6 }).map((_, i) => <TicketRowSkeleton key={i} />) : tickets.map((ticket) => <TicketRow key={ticket.id} ticket={ticket} projectId={id!} />)}</TableBody>
            </Table>
          )}
          {!isPending && tickets.length > 0 && (
            <div className="border-t px-4 py-3 flex items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{tickets.length}</span> tickets shown
              </p>
              {hasNextPage && (
                <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                  {isFetchingNextPage ? "Loading..." : "Load more"}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
